import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import api from '../services/api'
import { useAuth } from '../context/AppContext'
import { Plus } from 'lucide-react'
import ApiKeysModal from '../components/ApiKeysModal'

// Reglas responsive para esta página. Como todo el layout es por clases CSS
// (no estilos inline), un @media sí las alcanza. Se inyecta una sola vez.
const PARTNERS_RESPONSIVE_CSS = `
@media (max-width: 768px) {
  .partners-hero {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 16px !important;
  }
  .partners-toolbar-actions { width: 100% !important; flex-wrap: wrap !important; }
  .partners-toolbar-actions .btn { flex: 1 1 auto !important; justify-content: center !important; }

  .partners-stats-grid { grid-template-columns: 1fr 1fr !important; }
  .partners-grid { grid-template-columns: 1fr !important; }

  .partners-filters { flex-direction: column !important; align-items: stretch !important; }
  .partners-filters .search-bar,
  .partners-filters .select,
  .partners-filters .btn { width: 100% !important; }

  .partner-modal, .partner-modal-wide {
    width: 100% !important;
    max-width: 100% !important;
  }
  .partner-modal-grid-2 { grid-template-columns: 1fr !important; }
  .partner-modal-actions { flex-wrap: wrap !important; }
  .partner-modal-actions .btn { flex: 1 1 auto !important; justify-content: center !important; }
  .partner-secret-row { flex-wrap: wrap !important; }

  .partners-card-actions { flex-wrap: wrap !important; }

  /* iOS: evita el auto-zoom al enfocar inputs con fuente < 16px */
  .partner-modal .input,
  .partner-modal .select,
  .partner-secret-input,
  .partners-filters .input { font-size: 16px !important; }
}
`

const STATUS_BADGE = {
  active: 'badge-green',
  inactive: 'badge-amber',
  suspended: 'badge-red',
}

function pct(n, t) {
  return t ? Math.round((n / t) * 100) : 0
}

function getPartnerId(partner) {
  return partner?.id || partner?._id || ''
}

function normalizeNumeric(value) {
  return value === '' || value === null || value === undefined ? null : Number(value)
}

async function copyToClipboard(text, label = 'Valor') {
  try {
    await navigator.clipboard.writeText(text || '')
    toast.success(`${label} copied`)
  } catch {
    toast.error(`Could not copy ${label.toLowerCase()}`)
  }
}

function Field({ label, hint, children }) {
  return (
    <div className="partner-field">
      <label className="partner-field-label">{label}</label>
      {children}
      {hint ? <div className="partner-field-hint">{hint}</div> : null}
    </div>
  )
}

function SecretField({ label, value, copyLabel, helper, loading = false, editable = false, onChange }) {
  return (
    <Field label={label} hint={helper}>
      <div className="partner-secret-row">
        <input
          className="input partner-secret-input"
          value={loading ? 'Loading…' : value || ''}
          readOnly={!editable}
          onChange={editable ? onChange : undefined}
          placeholder={editable ? `Ingresa ${label}` : ''}
        />
        {!editable && (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            disabled={!value || loading}
            onClick={() => copyToClipboard(value, copyLabel || label)}
          >
            Copy
          </button>
        )}
      </div>
    </Field>
  )
}

function PartnerFormModal({ partner, canManagePartners, onClose, onSaved }) {
  const isEdit = !!partner
  const partnerId = partner?.id || partner?._id
  
  const [form, setForm] = useState(() => ({
    company_id: partner?.company_id || '',
    name: partner?.name || '',
    email: partner?.email || '',
    is_active: partner?.is_active !== false,
    is_live: partner?.is_live || false,
    daily_lead_limit: partner?.daily_lead_limit ?? '',
  }))
  
  const [pbForm, setPbForm] = useState({
    url: '',
    click_id_field: 'click_id',
    trigger_on: 'sold',
    is_active: true,
    params: {
      nid: '',
      transaction_id: '{click_id}',
      amount: '{amount}',
    }
  })

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [companies, setCompanies] = useState([])

  useEffect(() => {
    let ignore = false
    api.get('/companies/', { params: { limit: 50, page_size: 50, per_page: 50 } }).then(res => {
      if (!ignore) {
        const list = Array.isArray(res.data) ? res.data : (res.data?.items || res.data?.companies || res.data?.data || [])
        setCompanies(list)
      }
    }).catch(console.error)

    if (isEdit) {
      api.get(`/affiliates/${partnerId}/postback`)
        .then(res => {
          if (ignore) return
          const pb = res.data
          setPbForm({
            url: pb.url || '',
            click_id_field: pb.click_id_field || 'click_id',
            trigger_on: pb.trigger_on || 'sold',
            is_active: pb.is_active !== false,
            params: {
              nid: pb.params?.nid || '',
              transaction_id: pb.params?.transaction_id || '{click_id}',
              amount: pb.params?.amount || '{amount}',
            }
          })
        })
        .catch(() => {})
    }

    return () => { ignore = true }
  }, [isEdit, partnerId])

  const updateField = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const updatePbField = (key, value) => {
    setPbForm(prev => ({ ...prev, [key]: value }))
  }

  const updatePbParam = (key, value) => {
    setPbForm(prev => ({ ...prev, params: { ...prev.params, [key]: value } }))
  }

  async function save(event) {
    event.preventDefault()
    setSaving(true)
    setError('')

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim(),
        is_active: form.is_active,
        daily_lead_limit: form.daily_lead_limit === '' ? null : Number(form.daily_lead_limit),
      }

      let savedId = partnerId

      if (isEdit) {
        payload.is_live = form.is_live;
        await api.patch(`/affiliates/${partnerId}`, payload)
        toast.success('Affiliate updated')
      } else {
        if (!form.company_id) {
          setError('Please select a company first.')
          setSaving(false)
          return
        }
        payload.company_id = Number(form.company_id)
        const res = await api.post('/affiliates/', payload)
        savedId = res.data.id
        toast.success('Affiliate created')
      }

      // Handle Postback
      if (pbForm.url) {
        const pbPayload = {
          url: pbForm.url,
          click_id_field: pbForm.click_id_field,
          trigger_on: pbForm.trigger_on,
          is_active: pbForm.is_active,
          params: { ...pbForm.params }
        }
        if (!pbPayload.params.nid) delete pbPayload.params.nid
        
        await api.put(`/affiliates/${savedId}/postback`, pbPayload)
      } else if (isEdit) {
        await api.delete(`/affiliates/${savedId}/postback`).catch(() => {})
      }

      onSaved()
      onClose()
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error saving affiliate.'
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail))
    } finally {
      setSaving(false)
    }
  }

  const pbPreviewUrl = pbForm.url
    ? `${pbForm.url}?${pbForm.params.nid ? `nid=${pbForm.params.nid}&` : ''}transaction_id=${pbForm.params.transaction_id}&amount=${pbForm.params.amount}`
    : ''

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal partner-modal partner-modal-wide" onClick={event => event.stopPropagation()}>
        <div className="partner-modal-header">
          <div>
            <h2>{isEdit ? 'Edit affiliate' : 'New affiliate'}</h2>
            <p>
              {isEdit
                ? 'Update affiliate and postback settings.'
                : 'Create a new affiliate profile under a company.'}
            </p>
          </div>
          <button type="button" className="btn btn-ghost btn-icon" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form onSubmit={save} className="partner-modal-body" style={{ maxHeight: 'calc(100vh - 120px)', overflowY: 'auto' }}>
          <section className="partner-modal-section">
            <div className="partner-modal-section-head">
              <div>
                <h3>General Settings</h3>
                <p>Profile information and limits.</p>
              </div>
            </div>

            <div className="partner-modal-grid partner-modal-grid-2">
              <Field label="Affiliate Name *">
                <input
                  className="input"
                  value={form.name}
                  onChange={event => updateField('name', event.target.value)}
                  required
                  placeholder="e.g. LeadGen Pros"
                />
              </Field>

              <Field label="Email" hint="Required to enable Tapfiliate on this affiliate's campaigns.">
                <input
                  className="input"
                  type="email"
                  value={form.email}
                  onChange={event => updateField('email', event.target.value)}
                  placeholder="affiliate@example.com"
                />
              </Field>

              {!isEdit && (
                <Field label="Company *">
                  <select
                    className="select"
                    value={form.company_id}
                    onChange={event => updateField('company_id', event.target.value)}
                    required
                  >
                    <option value="">-- Select Company --</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </Field>
              )}

              <Field label="Daily Lead Limit" hint="Leave empty for unlimited.">
                <input
                  className="input"
                  type="number"
                  min="0"
                  value={form.daily_lead_limit}
                  onChange={event => updateField('daily_lead_limit', event.target.value)}
                  placeholder="Optional limit"
                />
              </Field>
            </div>
          </section>

          <section className="partner-modal-section">
            <div className="partner-modal-section-head">
              <div>
                <h3>Status & Traffic</h3>
                <p>Manage whether this affiliate is active or receiving live traffic.</p>
              </div>
            </div>

            <div className="partner-checkbox-row" style={{ display: 'flex', gap: '30px' }}>
              <label className="partner-check-label">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={event => updateField('is_active', event.target.checked)}
                />
                Is Active
              </label>

              {isEdit && (
                <label className="partner-check-label">
                  <input
                    type="checkbox"
                    checked={form.is_live}
                    onChange={event => updateField('is_live', event.target.checked)}
                  />
                  Is Live (Production)
                </label>
              )}
            </div>
          </section>

          <hr style={{ borderTop: '1px solid var(--border)', margin: '20px 0' }} />

          <section className="partner-modal-section">
            <div className="partner-modal-section-head">
              <div>
                <h3>Postback Configuration</h3>
                <p>Automatically notify the affiliate when leads are sold.</p>
              </div>
            </div>

            <Field label="Postback URL">
              <input
                className="input"
                placeholder="https://www.example.com/postback"
                value={pbForm.url}
                onChange={event => updatePbField('url', event.target.value)}
              />
            </Field>

            <div className="partner-modal-grid partner-modal-grid-2">
              <Field label="nid parameter">
                <input
                  className="input"
                  placeholder="1746"
                  value={pbForm.params.nid}
                  onChange={event => updatePbParam('nid', event.target.value)}
                />
              </Field>
              <Field label="Click ID Field">
                <input
                  className="input"
                  placeholder="click_id"
                  value={pbForm.click_id_field}
                  onChange={event => updatePbField('click_id_field', event.target.value)}
                />
              </Field>
              <Field label="transaction_id (value)">
                <input
                  className="input"
                  placeholder="{click_id}"
                  value={pbForm.params.transaction_id}
                  onChange={event => updatePbParam('transaction_id', event.target.value)}
                />
              </Field>
              <Field label="amount (value)">
                <input
                  className="input"
                  placeholder="{amount}"
                  value={pbForm.params.amount}
                  onChange={event => updatePbParam('amount', event.target.value)}
                />
              </Field>
              <Field label="Trigger On">
                <select
                  className="select"
                  value={pbForm.trigger_on}
                  onChange={event => updatePbField('trigger_on', event.target.value)}
                >
                  <option value="sold">Lead Sold</option>
                  <option value="no_winner">No Winner</option>
                </select>
              </Field>
            </div>

            <div className="partner-checkbox-row" style={{ marginTop: 12 }}>
              <label className="partner-check-label">
                <input
                  type="checkbox"
                  checked={pbForm.is_active}
                  onChange={event => updatePbField('is_active', event.target.checked)}
                />
                Postback active
              </label>
            </div>

            {pbPreviewUrl ? <div className="partner-inline-code partner-inline-code-wrap">{pbPreviewUrl}</div> : null}
          </section>

          {error ? <div className="partner-modal-error">{error}</div> : null}

          <div className="partner-modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Create affiliate'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}





function PartnerCard({ partner, canManagePartners, isAdmin, onEdit, onPostman, onPostback, onLeads, onKeys }) {
  const isLive = partner.is_live;
  const isActive = partner.is_active;
  const statusStr = isActive ? (isLive ? 'live' : 'testing') : 'inactive';
  
  let statusCls = 'badge-amber';
  if (isActive && isLive) statusCls = 'badge-green';
  if (!isActive) statusCls = 'badge-red';

  return (
    <div className="card fade-in partners-card">
      <div className="partners-card-head">
        <div className="partners-card-title-wrap">
          <div className="partners-card-title">{partner.name}</div>
          <div className="partners-card-subtitle">Affiliate ID: {partner.id} | Company ID: {partner.company_id}</div>
          <div className="partners-card-subtitle">{partner.email || 'No email on file'}</div>
        </div>
        <div className="partners-card-badges">
          {!partner.email && <span className="badge badge-amber" title="Required before Tapfiliate can provision this affiliate">No email</span>}
          <span className={`badge ${statusCls}`}>{statusStr}</span>
        </div>
      </div>


      <div className="partners-card-actions">
        <button className="btn btn-ghost partners-card-btn" onClick={() => onLeads(partner)}>Leads</button>
                {isAdmin && (
          <button className="btn btn-ghost partners-card-btn" onClick={() => onKeys(partner)}>Keys</button>
        )}
        {canManagePartners ? (
          <button className="btn btn-ghost partners-card-btn partners-card-btn-full" onClick={() => onEdit(partner)}>
            Edit affiliate
          </button>
        ) : null}
      </div>
    </div>
  )
}

export default function PartnersPage() {
  const navigate = useNavigate()
  const { user, isAdmin } = useAuth()
  const canManagePartners = isAdmin || user?.is_staff

  const [partners, setPartners] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [q, setQ] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [total, setTotal] = useState(0)
  const [summary, setSummary] = useState(null)
      const [editorFor, setEditorFor] = useState(null)
  const [keysFor, setKeysFor] = useState(null)

  const load = useCallback(async (search = '', status = '') => {
    setLoading(true)
    setError('')
    try {
      const params = new URLSearchParams()
      params.set('limit', '50')
      params.set('page_size', '50')
      params.set('per_page', '50')
      if (search) params.set('search', search)
      if (status) params.set('status', status)
      // The Django Ninja pagination returns { count: int, items: list }
      const res = await api.get(`/affiliates/?${params.toString()}`)
      
      const items = res.data?.items || res.data || []
      setPartners(items)
      setTotal(res.data?.count || items.length || 0)
      setSummary({ partners: res.data?.count || items.length || 0 })
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading affiliates.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  // Franja superior: usa el resumen GLOBAL del backend (todos los orígenes,
  // estado efectivo). Fallback a la suma de las tarjetas si no llega.
  const strip = useMemo(() => {
    if (summary) return summary
    const t = partners.reduce((sum, item) => sum + (item.stats?.total || 0), 0)
    const a = partners.reduce((sum, item) => sum + (item.stats?.accepted || 0), 0)
    const sd = partners.reduce((sum, item) => sum + (item.stats?.sold || 0), 0)
    return {
      partners: partners.length,
      total_leads: t,
      accepted: a,
      sold: sd,
      rejected: partners.reduce((sum, item) => sum + (item.stats?.rejected || 0), 0),
      acceptance_rate: t ? Math.round(((sd + a) / t) * 100) : 0,
      postbacks_on: partners.filter((item) => item.postback?.configured && item.postback?.isActive).length,
    }
  }, [partners, summary])

  return (
    <div className="fade-in page-shell partners-page-shell">
      <style>{PARTNERS_RESPONSIVE_CSS}</style>

      <section className="page-hero partners-hero">
        <div className="page-hero-content">
          <span className="hero-kicker">Affiliate operations</span>
          <h1>Manage credentials, intake access, routing and postbacks.</h1>
          <p>
            Keep each affiliate operational from one place: expose intake credentials, validate postback setup,
            review acceptance performance and jump directly into affiliate-specific leads.
          </p>
          <div className="hero-chips partners-header-badges">
            <span className="badge badge-blue">Operational panel</span>
            {canManagePartners
              ? <span className="badge badge-green">Create, edit and reveal credentials</span>
              : <span className="badge badge-amber">Read-only access for your current role</span>}
          </div>
        </div>
        <div className="page-hero-actions partners-toolbar-actions">
          <button className="btn btn-ghost" onClick={() => load(q, statusFilter)}>Refresh</button>
          {canManagePartners ? (
            <button className="btn btn-primary partners-create-btn" onClick={() => setEditorFor('create')}>
              + New affiliate
            </button>
          ) : null}
        </div>
      </section>

      {!loading ? (
        <div className="partners-stats-grid">
          {[
            { label: 'Affiliates', value: strip.partners },
          ].map((metric) => (
            <div key={metric.label} className="card partners-stat-card">
              <div className="partners-stat-value" style={metric.color ? { color: metric.color } : undefined}>{metric.value}</div>
              <div className="partners-stat-label">{metric.label}</div>
              {metric.sub ? (
                <div className="partners-stat-label" style={{ marginTop: 2, fontSize: '0.7rem', opacity: 0.75 }}>{metric.sub}</div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}

      <section className="card partners-filters partners-filters-panel">
        <div className="search-bar search-bar-elevated">
          <span className="search-icon">⌕</span>
          <input
            className="input"
            placeholder="Search by aff_id, name, email or alias…"
            value={q}
            onChange={(event) => setQ(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && load(q, statusFilter)}
          />
        </div>
        <select
          className="select"
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value)
            load(q, event.target.value)
          }}
        >
          <option value="">All statuses</option>
          <option value="live">Live</option>
          <option value="testing">Testing</option>
          <option value="inactive">Inactive</option>
        </select>
        <button className="btn btn-primary" onClick={() => load(q, statusFilter)}>Search</button>
        {canManagePartners ? (
          <button className="btn btn-primary partners-create-btn" onClick={() => setEditorFor('create')}>
            <Plus size={14} style={{ marginRight: 6 }} /> New affiliate
          </button>
        ) : null}
      </section>

      {loading ? <div className="partner-empty-block">Loading partners…</div> : null}
      {error ? <div className="partner-modal-error partner-page-error">{error}</div> : null}

      {!loading && !error && partners.length === 0 ? (
        <div className="card partner-empty-card">
          No affiliates registered.
          <br />
          <span>
            {canManagePartners
              ? 'You can create one using the “New affiliate” button.'
              : 'Ask a user with admin or operation permissions to create the affiliate.'}
          </span>
        </div>
      ) : null}

      {!loading && partners.length > 0 ? (
        <div className="partners-grid">
          {partners.map((item) => (
            <PartnerCard
              key={item.id}
              partner={item}
              canManagePartners={canManagePartners}
              isAdmin={isAdmin}
              onEdit={(item) => setEditorFor(item)}
                                          onLeads={(item) => navigate(`/partner-leads?partner_aff_id=${encodeURIComponent(item.aff_id || '')}`)}
              onKeys={(item) => setKeysFor(item)}
            />
          ))}
        </div>
      ) : null}

      {editorFor ? (
        <PartnerFormModal
          partner={editorFor === 'create' ? null : editorFor}
          canManagePartners={canManagePartners}
          onClose={() => setEditorFor(null)}
          onSaved={() => load(q, statusFilter)}
        />
      ) : null}

      

      {keysFor && (
        <ApiKeysModal
          affiliateId={keysFor.id}
          affiliateName={keysFor.name}
          onClose={() => setKeysFor(null)}
        />
      )}

      
    </div>
  )
}
